import moment from "moment-timezone";

// 比照 POS，實際效期優先；舊 API 未提供時回退領取時的原始效期。
export function getCouponValidFromAt(coupon) {
  return coupon?.effective_valid_from_at ?? coupon?.valid_from_at;
}

export function getCouponValidUntilAt(coupon) {
  return coupon?.effective_valid_until_at ?? coupon?.valid_until_at;
}

export function isCouponExpired(coupon, nowMs = Date.now()) {
  const validUntilMs = Date.parse(getCouponValidUntilAt(coupon));
  return Number.isFinite(validUntilMs) && nowMs >= validUntilMs;
}

// 終態與占用狀態優先；時間只衍生展示狀態，不改寫 API 資料。
export function getCouponStatus(coupon, nowMs = Date.now()) {
  const terminalStates = ["used", "expired", "revoked"];
  if (terminalStates.includes(coupon?.status)) return coupon.status;
  // selected：已被未結的線上訂單選用，不是已使用，也不可再出示 QR
  if (coupon?.status === "selected") return "selected";
  if (coupon?.status === "redeeming") return "redeeming";
  if (coupon?.status === "available") {
    const effectiveStatus = coupon.effective_status;
    if (["available", "not_started"].includes(effectiveStatus)) {
      return isCouponExpired(coupon, nowMs) ? "expired" : effectiveStatus;
    }
    return [...terminalStates, "selected", "redeeming", "disabled_by_template"].includes(effectiveStatus)
      ? effectiveStatus : "unknown";
  }
  return "unknown";
}

// 尚未生效只影響顯示與按鈕，不改寫 API status
export function isCouponNotYetValid(coupon, nowMs = Date.now()) {
  const validFromMs = Date.parse(getCouponValidFromAt(coupon));
  return (
    coupon?.status === "available" &&
    ["available", "not_started"].includes(coupon?.effective_status) &&
    !isCouponExpired(coupon, nowMs) &&
    (coupon?.effective_status === "not_started" ||
      (Number.isFinite(validFromMs) && nowMs < validFromMs))
  );
}

export function canUseCoupon(coupon, nowMs = Date.now()) {
  const validFromMs = Date.parse(getCouponValidFromAt(coupon));
  return (
    coupon?.status === "available" &&
    getCouponStatus(coupon, nowMs) === "available" &&
    Number.isFinite(validFromMs) &&
    nowMs >= validFromMs &&
    coupon?.template_status === "active" &&
    !coupon?.unavailable_reason &&
    typeof coupon?.qr_payload === "string" &&
    coupon.qr_payload.trim().length > 0
  );
}

// 沿用會員頁既有終態文案，並補齊實際狀態 not_started；不顯示 raw status。
// 舊 API 尚未生效仍屬可使用；新版 not_started 顯示尚未開始。
export function getCouponStatusText(coupon, nowMs = Date.now()) {
  const statusText = {
    available: "可使用",
    not_started: "尚未開始",
    selected: "已選用",
    // 歷史 redeeming 仍占用名額、尚未核銷完成，歸為已選用
    redeeming: "已選用",
    used: "已使用",
    expired: "已失效",
    revoked: "已失效",
    disabled_by_template: "已失效",
    unknown: "暫時無法使用",
  };
  return statusText[getCouponStatus(coupon, nowMs)] || statusText.unknown;
}

// 尚未生效時回傳「YYYY/MM/DD 起可使用」，其他情況回空字串
export function getCouponValidFromText(coupon, nowMs = Date.now()) {
  if (!isCouponNotYetValid(coupon, nowMs)) return "";
  const validFrom = formatCouponDateTime(
    getCouponValidFromAt(coupon),
    coupon.timezone_name
  );
  return validFrom ? `${validFrom} 起可使用` : "";
}

// 以店家時區（API 的 timezone_name）顯示，不套用瀏覽器時區；無法解析時保留原字串
// 優惠券起訖固定為 00:00:00／23:59:59，所有日期只顯示到日
export const COUPON_DATE_FORMAT = "YYYY/MM/DD";
export function formatCouponDateTime(
  value,
  timezoneName = "Asia/Taipei",
  format = COUPON_DATE_FORMAT
) {
  if (!value) return "";
  const time = moment.parseZone(value, moment.ISO_8601, true);
  if (!time.isValid()) return String(value);
  const zone = moment.tz.zone(timezoneName) ? timezoneName : "Asia/Taipei";
  return time.tz(zone).format(format);
}

// 依契約顯示中文；只有生日情境採用後端 msg
// 選券相關錯誤碼（COUPON_ALREADY_SELECTED 等）屬線上點餐用券，暫緩
const COUPON_ERROR_MESSAGES = {
  // 舊契約查無公開母券；新版契約 §14 說明現行查無時回 COUPON_TEMPLATE_NOT_FOUND
  COUPON_PUBLIC_TEMPLATE_NOT_FOUND: "查無可領取的優惠活動",
  COUPON_PUBLIC_OFFER_NOT_FOUND: "此領券連結已失效或無法使用",
  COUPON_TEMPLATE_NOT_FOUND: "查無此優惠活動",
  COUPON_MEMBER_STORE_SCOPE_INVALID: "會員不屬於此店或店籍已停用",
  COUPON_OFFER_NOT_AVAILABLE: "活動尚未開始、已結束或領取入口已失效",
  COUPON_OFFER_TARGET_MISMATCH: "此優惠不屬於目前會員",
  COUPON_TEMPLATE_NOT_ACTIVE: "活動目前不可領取",
  COUPON_ISSUE_LIMIT_REACHED: "優惠券已領完",
  ALREADY_CLAIMED_IN_PERIOD: "本期已領取過此優惠券",
  COUPON_ACTIVE_LIMIT_REACHED: "已達持有上限，請先使用或等待現有優惠券到期",
  COUPON_MEMBER_NOT_ELIGIBLE: "不符合領取資格",
  COUPON_BIRTHDAY_REQUIRED: "請先填寫生日後再領取",
  COUPON_BIRTHDAY_MONTH_MISMATCH: "此優惠僅限當月壽星領取",
  COUPON_MEMBER_COUPON_NOT_FOUND: "查無此優惠券",
  COUPON_RATE_LIMITED: "操作過於頻繁，請稍後再試",
  COUPON_RATE_LIMIT_UNAVAILABLE: "服務忙碌中，請稍後再試",
  IDEMPOTENCY_CONFLICT: "領取請求衝突，請重新整理後再試",
};

export const ALREADY_CLAIMED_IN_PERIOD = "ALREADY_CLAIMED_IN_PERIOD";
export const COUPON_BIRTHDAY_REQUIRED = "COUPON_BIRTHDAY_REQUIRED";
export const COUPON_BIRTHDAY_MONTH_MISMATCH = "COUPON_BIRTHDAY_MONTH_MISMATCH";
export const BIRTHDAY_UNREADABLE_TEXT = "會員生日資料無法辨識，請聯繫店家協助更正。";
export const COUPON_ACTIVE_LIMIT_REACHED = "COUPON_ACTIVE_LIMIT_REACHED";

// 限流、處理中與無錯誤碼（網路／未知）可由會員再按一次；其餘業務錯誤不可重試繞過
export function isRetryableCouponError(code) {
  return (
    !code ||
    code === "COUPON_RATE_LIMITED" ||
    code === "COUPON_RATE_LIMIT_UNAVAILABLE" ||
    code === "COUPON_REQUEST_IN_PROGRESS"
  );
}

// 回傳給 UI 的錯誤只帶中文訊息與 error_code，不含 Axios config／request body
export function createCouponError(errorCode, status, metadata = {}) {
  const code =
    typeof errorCode === "string" && errorCode
      ? errorCode
      : status === 429
      ? "COUPON_RATE_LIMITED"
      : "";
  const isBirthdayAlreadyClaimed = code === ALREADY_CLAIMED_IN_PERIOD &&
    metadata.details?.campaign_type === "birthday";
  const isBirthdayError = isBirthdayAlreadyClaimed ||
    [COUPON_BIRTHDAY_REQUIRED, COUPON_BIRTHDAY_MONTH_MISMATCH].includes(code);
  const message = typeof metadata.msg === "string" ? metadata.msg.trim() : "";
  const fallback = isBirthdayAlreadyClaimed
    ? "今年已領取過生日禮"
    : COUPON_ERROR_MESSAGES[code] || "優惠券操作失敗，請稍後再試";
  const error = new Error(isBirthdayError && message ? message : fallback);
  error.code = code;
  error.httpStatus = status;
  error.retryable = metadata.retryable === true;
  error.details = metadata.details && typeof metadata.details === "object" && !Array.isArray(metadata.details)
    ? metadata.details : {};
  error.requestId = typeof metadata.request_id === "string" ? metadata.request_id : "";
  error.retryAfter = metadata.retryAfter;
  return error;
}

const CHECKOUT_ERROR_MESSAGES = {
  COUPON_NO_EFFECTIVE_BENEFIT: "此優惠券不適用目前訂單，請重新選擇",
  COUPON_TEMPLATE_REVISION_CONFLICT: "優惠內容已變更，請確認最新金額",
  COUPON_ORDER_PRICE_MISMATCH: "訂單優惠金額需要重新確認，請稍後再試",
  COUPON_MEMBER_COUPON_NOT_FOUND: "此優惠券目前無法使用，請重新選擇",
  COUPON_MEMBER_COUPON_NOT_AVAILABLE: "此優惠券目前無法使用，請重新選擇",
  COUPON_MEMBER_STORE_SCOPE_INVALID: "此優惠券目前無法使用，請重新選擇",
  COUPON_TEMPLATE_NOT_ACTIVE: "優惠活動已停止，請重新選擇",
  COUPON_INVALID_CONTRACT: "訂單優惠資料不完整，請重新確認",
  COUPON_ONLINE_ORDER_SNAPSHOT_MISSING: "訂單優惠資料不完整，請重新確認",
  COUPON_CAPABILITY_NOT_ENABLED: "優惠券功能暫時無法使用，請稍後再試",
  COUPON_RATE_LIMITED: "操作過於頻繁或服務忙碌，請稍後再試",
  COUPON_RATE_LIMIT_UNAVAILABLE: "操作過於頻繁或服務忙碌，請稍後再試",
};

export function getCouponCheckoutErrorMessage(error) {
  return CHECKOUT_ERROR_MESSAGES[error?.code] || "優惠券確認失敗，請稍後再試";
}
